import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  TextField,
  Popper,
  Paper,
  ClickAwayListener,
  IconButton,
  Divider,
  Button,
  Typography,
  Autocomplete,
  Chip,
  CircularProgress,
  Tooltip,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import { getGridButtonSelectorStyles } from "../theme";
import {
  POPPER_CONFIG,
  POPPER_WIDTHS,
  PAPER_CONFIG,
  DATE_RANGE_CONFIG,
  GRID_SIZES,
  SPACING,
} from "../constants/filterSelectors";

const DEFAULT_SERVICE_TYPES_BY_CATEGORY = {
  Translation: [],
  Interpretation: [],
  Other: [],
};

const getNormalizedSelectedTypes = (
  serviceTypesByCategory,
  selectedServiceTypesByCategory,
) => ({
  Translation:
    selectedServiceTypesByCategory?.Translation ||
    serviceTypesByCategory.Translation ||
    [],
  Interpretation:
    selectedServiceTypesByCategory?.Interpretation ||
    serviceTypesByCategory.Interpretation ||
    [],
  Other:
    selectedServiceTypesByCategory?.Other || serviceTypesByCategory.Other || [],
});

/**
 * ReportsFilterSelector - Comprehensive filter component for Reports screen
 *
 * Combines:
 * - Multi-month and multi-year date range selection
 * - Client multi-select
 * - Linguist multi-select
 * - Service category selection
 *
 * Props:
 * - months: Array of month abbreviations
 * - years: Array of year strings
 * - selectedMonths: Array of selected month abbreviations
 * - selectedYears: Array of selected year strings
 * - clients: Array of { id, label } objects
 * - selectedClients: Array of client IDs
 * - linguists: Array of { id, label } objects
 * - selectedLinguists: Array of linguist IDs
 * - serviceTypesByCategory: Object with 3 category keys and list of types
 * - selectedServiceTypesByCategory: Object with selected types per category
 * - onSave: Callback function with all filter values
 * - loading: Boolean for loading state
 */
const ReportsFilterSelector = ({
  months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  years = [],
  selectedMonths = [],
  selectedYears = [],
  clients = [],
  selectedClients = [],
  linguists = [],
  selectedLinguists = [],
  serviceTypesByCategory = DEFAULT_SERVICE_TYPES_BY_CATEGORY,
  selectedServiceTypesByCategory = DEFAULT_SERVICE_TYPES_BY_CATEGORY,
  onSave,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const triggerBoxShadow =
    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)";
  const textFieldRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Local state for filters (temporary until Save is clicked)
  const [localSelectedMonths, setLocalSelectedMonths] =
    useState(selectedMonths);
  const [localSelectedYears, setLocalSelectedYears] = useState(selectedYears);
  const [localSelectedClients, setLocalSelectedClients] =
    useState(selectedClients);
  const [localSelectedLinguists, setLocalSelectedLinguists] =
    useState(selectedLinguists);
  const [
    localSelectedServiceTypesByCategory,
    setLocalSelectedServiceTypesByCategory,
  ] = useState(
    getNormalizedSelectedTypes(
      serviceTypesByCategory,
      selectedServiceTypesByCategory,
    ),
  );
  const [openServiceTypeDropdowns, setOpenServiceTypeDropdowns] = useState({
    Translation: false,
    Interpretation: false,
    Other: false,
  });

  // Sync local state with props
  useEffect(() => {
    setLocalSelectedMonths(selectedMonths);
    setLocalSelectedYears(selectedYears);
    setLocalSelectedClients(selectedClients);
    setLocalSelectedLinguists(selectedLinguists);
    setLocalSelectedServiceTypesByCategory(
      getNormalizedSelectedTypes(
        serviceTypesByCategory,
        selectedServiceTypesByCategory,
      ),
    );
  }, [
    selectedMonths,
    selectedYears,
    selectedClients,
    selectedLinguists,
    serviceTypesByCategory,
    selectedServiceTypesByCategory,
  ]);

  const handleClick = () => {
    setAnchorEl(anchorEl ? null : textFieldRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMonthToggle = (month) => {
    setLocalSelectedMonths((prev) => {
      if (prev.includes(month)) {
        return prev.filter((m) => m !== month);
      } else {
        return [...prev, month];
      }
    });
  };

  const handleYearToggle = (year) => {
    setLocalSelectedYears((prev) => {
      if (prev.includes(year)) {
        return prev.filter((y) => y !== year);
      } else {
        return [...prev, year];
      }
    });
  };

  const getTotalSelectedServiceTypes = (selectedTypesByCategory) =>
    Object.values(selectedTypesByCategory).reduce(
      (sum, types) => sum + types.length,
      0,
    );

  const handleServiceTypeToggle = (category, type) => {
    setLocalSelectedServiceTypesByCategory((prev) => {
      const categoryTypes = prev[category] || [];
      const isSelected = categoryTypes.includes(type);

      const updatedCategoryTypes = isSelected
        ? categoryTypes.filter((t) => t !== type)
        : [...categoryTypes, type];

      return {
        ...prev,
        [category]: updatedCategoryTypes,
      };
    });
  };

  const toggleServiceTypeDropdown = (category) => {
    setOpenServiceTypeDropdowns((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    const selectedServiceCategories = Object.entries(
      localSelectedServiceTypesByCategory,
    )
      .filter(([, selectedTypes]) => selectedTypes.length > 0)
      .map(([category]) => category);

    if (onSave) {
      onSave({
        months: localSelectedMonths,
        years: localSelectedYears,
        clients: localSelectedClients,
        linguists: localSelectedLinguists,
        serviceCategories: selectedServiceCategories,
        serviceTypesByCategory: localSelectedServiceTypesByCategory,
        translationTypes: localSelectedServiceTypesByCategory.Translation,
        interpretationTypes: localSelectedServiceTypesByCategory.Interpretation,
        otherServiceTypes: localSelectedServiceTypesByCategory.Other,
      });
    }
    handleClose();
  };

  const handleClearFilters = () => {
    // Get current month and year
    const currentDate = new Date();
    const currentMonth = months[currentDate.getMonth()]; // Get current month abbreviation
    const currentYear = String(currentDate.getFullYear());

    // Reset to current month and year only, with all service categories selected
    setLocalSelectedMonths([currentMonth]);
    setLocalSelectedYears(years.includes(currentYear) ? [currentYear] : []);
    setLocalSelectedClients([]);
    setLocalSelectedLinguists([]);
    setLocalSelectedServiceTypesByCategory({
      Translation: [...(serviceTypesByCategory.Translation || [])],
      Interpretation: [...(serviceTypesByCategory.Interpretation || [])],
      Other: [...(serviceTypesByCategory.Other || [])],
    });

    if (onSave) {
      onSave({
        months: [currentMonth],
        years: years.includes(currentYear) ? [currentYear] : [],
        clients: [],
        linguists: [],
        serviceCategories: ["Translation", "Interpretation", "Other"],
        serviceTypesByCategory: {
          Translation: [...(serviceTypesByCategory.Translation || [])],
          Interpretation: [...(serviceTypesByCategory.Interpretation || [])],
          Other: [...(serviceTypesByCategory.Other || [])],
        },
        translationTypes: [...(serviceTypesByCategory.Translation || [])],
        interpretationTypes: [...(serviceTypesByCategory.Interpretation || [])],
        otherServiceTypes: [...(serviceTypesByCategory.Other || [])],
      });
    }
    handleClose();
  };

  const getDisplayText = () => {
    const totalSelectedServiceTypes = getTotalSelectedServiceTypes(
      localSelectedServiceTypesByCategory,
    );

    const filterCount =
      localSelectedMonths.length +
      localSelectedYears.length +
      localSelectedClients.length +
      localSelectedLinguists.length +
      totalSelectedServiceTypes;

    if (filterCount === 0) {
      return "No filters applied";
    }

    const parts = [];
    if (localSelectedMonths.length > 0) {
      parts.push(
        `${localSelectedMonths.length} month${localSelectedMonths.length > 1 ? "s" : ""}`,
      );
    }
    if (localSelectedYears.length > 0) {
      parts.push(
        `${localSelectedYears.length} year${localSelectedYears.length > 1 ? "s" : ""}`,
      );
    }
    if (localSelectedClients.length > 0) {
      parts.push(
        `${localSelectedClients.length} client${localSelectedClients.length > 1 ? "s" : ""}`,
      );
    }
    if (localSelectedLinguists.length > 0) {
      parts.push(
        `${localSelectedLinguists.length} linguist${localSelectedLinguists.length > 1 ? "s" : ""}`,
      );
    }
    if (totalSelectedServiceTypes > 0) {
      parts.push(
        `${totalSelectedServiceTypes} type${totalSelectedServiceTypes > 1 ? "s" : ""}`,
      );
    }

    return parts.join(", ");
  };

  const open = Boolean(anchorEl);
  const hasActiveFilter =
    localSelectedMonths.length > 0 ||
    localSelectedYears.length > 0 ||
    localSelectedClients.length > 0 ||
    localSelectedLinguists.length > 0 ||
    getTotalSelectedServiceTypes(localSelectedServiceTypesByCategory) > 0;

  const isApplyDisabled =
    localSelectedMonths.length === 0 ||
    getTotalSelectedServiceTypes(localSelectedServiceTypesByCategory) === 0;

  const renderSideSections = () => (
    <Box sx={{ flex: 1 }}>
      {/* Clients Section */}
      <Box sx={{ mb: SPACING.containerGap }}>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            fontWeight: "bold",
            color: theme.palette.primary.text2,
          }}
        >
          Clients
        </Typography>
        <Autocomplete
          multiple
          options={clients}
          value={clients.filter((c) => localSelectedClients.includes(c.id))}
          onChange={(event, newValue) => {
            setLocalSelectedClients(newValue.map((v) => v.id));
          }}
          getOptionLabel={(option) => option.label}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select clients..."
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.label}
                size="small"
              />
            ))
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: theme.palette.background.default,
            },
          }}
        />
      </Box>

      {/* Linguists Section */}
      <Box sx={{ mb: SPACING.containerGap }}>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            fontWeight: "bold",
            color: theme.palette.primary.text2,
          }}
        >
          Linguists
        </Typography>
        <Autocomplete
          multiple
          options={linguists}
          value={linguists.filter((l) => localSelectedLinguists.includes(l.id))}
          onChange={(event, newValue) => {
            setLocalSelectedLinguists(newValue.map((v) => v.id));
          }}
          getOptionLabel={(option) => option.label}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select linguists..."
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.label}
                size="small"
              />
            ))
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: theme.palette.background.default,
            },
          }}
        />
      </Box>

      {/* Service Types by Category Section */}
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            fontWeight: "bold",
            color: theme.palette.primary.text2,
          }}
        >
          Service Types
        </Typography>
        <Grid container spacing={SPACING.gridSpacing}>
          {Object.keys(serviceTypesByCategory).map((category) => {
            const categoryTypes = serviceTypesByCategory[category] || [];
            const selectedTypes =
              localSelectedServiceTypesByCategory[category] || [];
            const selectedCount = selectedTypes.length;
            const totalCount = categoryTypes.length;
            const countLabel =
              selectedCount === totalCount
                ? "ALL"
                : `${selectedCount}/${totalCount}`;

            return (
              <Grid size={12} key={category}>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.primary.border2}`,
                    borderRadius: "12px",
                    bgcolor: theme.palette.background.default,
                  }}
                >
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      value={`${category} [${countLabel}]`}
                      onClick={() => toggleServiceTypeDropdown(category)}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleServiceTypeDropdown(category);
                            }}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <ArrowDropDownIcon
                              sx={{
                                transform: openServiceTypeDropdowns[category]
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </IconButton>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: theme.palette.background.default,
                        },
                        "& .MuiInputBase-input": {
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Box>

                  {openServiceTypeDropdowns[category] && (
                    <Box
                      sx={{
                        px: 1.25,
                        pb: 1,
                        borderTop: `1px solid ${theme.palette.primary.border2}`,
                      }}
                    >
                      <FormGroup>
                        {categoryTypes.map((type) => {
                          const isChecked = selectedTypes.includes(type);

                          return (
                            <FormControlLabel
                              key={type}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  size="small"
                                  onChange={() =>
                                    handleServiceTypeToggle(category, type)
                                  }
                                />
                              }
                              label={type}
                              sx={{
                                "& .MuiFormControlLabel-label": {
                                  fontSize: "0.85rem",
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                          );
                        })}
                      </FormGroup>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {isMobile ? (
        <Tooltip title="Reports Filters">
          <IconButton
            onClick={handleClick}
            ref={textFieldRef}
            size="small"
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.background.default,
              boxShadow:
                "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "12px",
              border: "none",
              color: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.buttonHover,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <FilterListRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <TextField
          ref={textFieldRef}
          fullWidth
          size="small"
          label="Filters"
          value={getDisplayText()}
          onClick={handleClick}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
            height: "100%",
            "& .MuiInputBase-root": {
              height: "100%",
              display: "flex",
              alignItems: "center",
              boxShadow: triggerBoxShadow,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.default,
            },
            "& .MuiInputBase-input": {
              py: "8px !important",
              color: theme.palette.text.primary,
              fontSize: "0.875rem",
            },
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton
                edge="end"
                onClick={handleClick}
                sx={{ padding: 0.5 }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            ),
          }}
        />
      )}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: POPPER_CONFIG.zIndex }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={PAPER_CONFIG.elevation}
            sx={{
              p: PAPER_CONFIG.padding,
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.background.default}`,
              borderRadius: PAPER_CONFIG.borderRadius,
              maxHeight: "80vh",
              overflowY: "auto",
              width: isMobile ? "min(90vw, 360px)" : POPPER_WIDTHS.reports,
            }}
          >
            {/* Main Layout: Left (Months/Years) | Right (Clients/Linguists/Categories) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: SPACING.containerGap,
              }}
            >
              {/* Left Column: Months and Years */}
              <Box sx={{ width: isMobile ? "100%" : DATE_RANGE_CONFIG.width }}>
                {/* Months Section */}
                <Box sx={{ mb: SPACING.containerGap }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    Months
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Button
                      onClick={() => {
                        setLocalSelectedMonths([...months]);
                      }}
                      size="small"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={() => {
                        setLocalSelectedMonths([]);
                      }}
                      size="small"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      Select None
                    </Button>
                  </Box>
                  <Grid container spacing={SPACING.gridSpacing}>
                    {months.map((month) => {
                      const isSelected = localSelectedMonths.includes(month);
                      return (
                        <Grid size={GRID_SIZES.month} key={month}>
                          <Box
                            sx={getGridButtonSelectorStyles(theme, isSelected)}
                            onClick={() => handleMonthToggle(month)}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: isSelected
                                  ? "#ffffff"
                                  : theme.palette.primary.main,
                                fontWeight: isSelected ? "bold" : "normal",
                              }}
                            >
                              {month}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                {/* Years Section */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    Years
                  </Typography>
                  <Grid container spacing={SPACING.gridSpacing}>
                    {years.map((year) => {
                      const isSelected = localSelectedYears.includes(year);
                      return (
                        <Grid size={GRID_SIZES.year} key={year}>
                          <Box
                            sx={getGridButtonSelectorStyles(theme, isSelected)}
                            onClick={() => handleYearToggle(year)}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: isSelected
                                  ? "#ffffff"
                                  : theme.palette.primary.main,
                                fontWeight: isSelected ? "bold" : "normal",
                              }}
                            >
                              {year}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>
              {isMobile ? (
                <Box sx={{ width: "100%" }}>
                  <Divider sx={{ my: SPACING.containerGap }} />
                  {renderSideSections()}
                </Box>
              ) : (
                <>
                  <Divider orientation="vertical" flexItem />
                  {renderSideSections()}
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {hasActiveFilter ? (
                <Button
                  onClick={handleClearFilters}
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<ClearIcon />}
                >
                  Clear All Filters
                </Button>
              ) : (
                <Box />
              )}

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isApplyDisabled}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: "12px",
                  "&:hover": {
                    bgcolor: theme.palette.primary.buttonHoverSelected,
                  },
                  ...(isApplyDisabled && {
                    bgcolor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                    cursor: "not-allowed",
                  }),
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default ReportsFilterSelector;
